/**
 * Pre-Lab Assessment Component
 * Students take a quiz before accessing the lab experiment
 */

import { useState } from 'react';
import { Check, X, AlertCircle, Lock, Unlock, Eye, EyeOff } from 'lucide-react';

interface PreLabQuestion {
    id: string;
    type: 'mcq' | 'true-false' | 'short-answer';
    question: string;
    options?: string[];
    correctAnswer: string | number;
    points: number;
    explanation: string;
}

/**
 * Grade a short-answer response by keyword overlap. Far more forgiving
 * than exact-string equality — students can paraphrase, reorder, or skip
 * punctuation and still be marked correct.
 *
 * Algorithm:
 *  1. Strip punctuation and lowercase both the canonical answer and the
 *     student's answer.
 *  2. Tokenize each into words; remove stop words and very short tokens.
 *  3. Compute the fraction of "significant" canonical keywords that
 *     appear in the student's answer.
 *  4. Accept if the overlap is ≥ 0.55 (about half the keywords).
 *  5. Accept any answer that is verbatim equal (after normalisation) as
 *     a fast path.
 */
const STOP_WORDS = new Set<string>([
    'a', 'an', 'and', 'are', 'as', 'at', 'be', 'by', 'for', 'from', 'has',
    'have', 'in', 'into', 'is', 'it', 'its', 'of', 'on', 'or', 'so', 'such',
    'than', 'that', 'the', 'their', 'then', 'there', 'these', 'they', 'this',
    'to', 'too', 'until', 'was', 'were', 'when', 'where', 'which', 'while',
    'will', 'with', 'would', 'do', 'does', 'did', 'can', 'could', 'should',
    'shall', 'may', 'might', 'must', 'i', 'we', 'you', 'your', 'our', 'all',
    'any', 'each', 'every', 'no', 'not', 'just', 'only', 'also', 'more',
    'most', 'some', 'over', 'under', 'about', 'than', 'because', 'how',
    'what', 'why', 'who', 'whose', 'if', 'then', 'before', 'after', 'during',
    'between', 'against', 'through', 'up', 'down', 'out', 'off', 'above',
    'below',
]);

function normalize(s: string): string[] {
    return s
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')   // strip punctuation
        .split(/\s+/)
        .map(w => w.trim())
        .filter(w => w.length > 2 && !STOP_WORDS.has(w));
}

export function gradeShortAnswer(correct: string, user: string): boolean {
    const correctRaw = correct.toLowerCase().replace(/\s+/g, ' ').trim();
    const userRaw = user.toLowerCase().replace(/\s+/g, ' ').trim();
    if (!userRaw) return false;
    // Fast path: identical after normalisation
    if (correctRaw === userRaw) return true;
    // Fast path: student wrote the canonical answer as a substring of their own
    if (userRaw.includes(correctRaw) || correctRaw.includes(userRaw)) return true;
    // Keyword-overlap path
    const correctWords = normalize(correct);
    if (correctWords.length === 0) return correctRaw === userRaw;
    const userWords = new Set(normalize(user));
    let hits = 0;
    for (const w of correctWords) {
        if (userWords.has(w)) hits += 1;
    }
    const overlap = hits / correctWords.length;
    return overlap >= 0.55;
}

interface PreLabAssessmentProps {
    questions: PreLabQuestion[];
    passingScore: number;
    maxAttempts: number;
    showAnswersAfterFail: boolean;
    allowTeacherBypass?: boolean;
    onPass: (answers: { questionId: string; answer: string | number }[]) => void;
    onBypass?: (password: string) => void;
}

export default function PreLabAssessment({
    questions,
    passingScore,
    maxAttempts,
    showAnswersAfterFail,
    allowTeacherBypass = true,
    onPass,
    onBypass,
}: PreLabAssessmentProps) {
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string | number>>({});
    const [submitted, setSubmitted] = useState(false);
    const [score, setScore] = useState(0);
    const [passed, setPassed] = useState(false);
    const [attemptNumber, setAttemptNumber] = useState(1);
    const [showBypassModal, setShowBypassModal] = useState(false);

    const currentQuestion = questions[currentQuestionIndex];
    const totalQuestions = questions.length;
    const isLastQuestion = currentQuestionIndex === totalQuestions - 1;

    const handleAnswerChange = (value: string | number) => {
        setAnswers({
            ...answers,
            [currentQuestion.id]: value,
        });
    };

    const handleNext = () => {
        if (!isLastQuestion) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    const handleSubmit = () => {
        // Calculate score
        let correctCount = 0;
        let totalPoints = 0;

        questions.forEach((q) => {
            totalPoints += q.points;
            const userAnswer = answers[q.id];

            if (q.type === 'mcq' || q.type === 'true-false') {
                if (userAnswer === q.correctAnswer) {
                    correctCount += q.points;
                }
            } else if (q.type === 'short-answer') {
                if (gradeShortAnswer(String(q.correctAnswer), String(userAnswer || ''))) {
                    correctCount += q.points;
                }
            }
        });

        const percentage = (correctCount / totalPoints) * 100;
        setScore(percentage);
        setSubmitted(true);

        if (percentage >= passingScore) {
            setPassed(true);
            setTimeout(() => {
                const formattedAnswers = Object.entries(answers).map(([key, val]) => ({ questionId: key, answer: val }));
                onPass(formattedAnswers);
            }, 2000);
        }
    };

    const handleRetry = () => {
        setCurrentQuestionIndex(0);
        setAnswers({});
        setSubmitted(false);
        setScore(0);
        setAttemptNumber(attemptNumber + 1);
    };

    const handleBypass = (password: string) => {
        if (onBypass) {
            onBypass(password);
        }
    };

    // Results view after submission
    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-pink-900 flex items-center justify-center p-4">
                <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8">
                    {passed ? (
                        <div className="text-center">
                            <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Check className="w-12 h-12 text-green-600" />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                                Congratulations!
                            </h2>
                            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                                You passed the pre-lab assessment!
                            </p>
                            <div className="text-5xl font-bold text-green-600 mb-2">
                                {score.toFixed(1)}%
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-8">
                                Passing score: {passingScore}%
                            </p>
                            <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <Unlock className="w-4 h-4" />
                                <span>Redirecting to lab experiment...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center">
                            <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                                <X className="w-12 h-12 text-red-600" />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                                Not Quite There
                            </h2>
                            <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                                You need {passingScore}% to access the lab
                            </p>
                            <div className="text-5xl font-bold text-red-600 mb-2">
                                {score.toFixed(1)}%
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-8">
                                Attempt {attemptNumber} of {maxAttempts}
                            </p>

                            {showAnswersAfterFail && (
                                <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <h3 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                                        Review Your Answers
                                    </h3>
                                    <div className="text-left space-y-2 text-sm">
                                        {questions.map((q, index) => {
                                            const userAnswer = answers[q.id];
                                            const isCorrect = q.type === 'short-answer'
                                                ? gradeShortAnswer(String(q.correctAnswer), String(userAnswer || ''))
                                                : userAnswer === q.correctAnswer;

                                            return (
                                                <div key={q.id} className="flex items-start gap-2">
                                                    {isCorrect ? (
                                                        <Check className="w-4 h-4 text-green-600 mt-0.5" />
                                                    ) : (
                                                        <X className="w-4 h-4 text-red-600 mt-0.5" />
                                                    )}
                                                    <div className="flex-1">
                                                        <p className="text-gray-900 dark:text-white">
                                                            Q{index + 1}: {isCorrect ? 'Correct' : `Correct answer: ${q.correctAnswer}`}
                                                        </p>
                                                        {!isCorrect && q.explanation && (
                                                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                                {q.explanation}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {attemptNumber < maxAttempts ? (
                                <button
                                    onClick={handleRetry}
                                    className="px-8 py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
                                >
                                    Try Again
                                </button>
                            ) : (
                                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                    <p className="text-red-900 dark:text-red-300 font-medium">
                                        Maximum attempts reached. Please contact your teacher.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Quiz view
    return (
        <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-pink-900 flex items-center justify-center p-4">
            <div className="max-w-3xl w-full">
                {/* Header */}
                <div className="bg-white dark:bg-gray-800 rounded-t-2xl shadow-lg border border-gray-200 dark:border-gray-700 border-b-0 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                                Pre-Lab Assessment
                            </h1>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Complete this quiz to unlock the lab experiment
                            </p>
                        </div>
                        {allowTeacherBypass && (
                            <button
                                onClick={() => setShowBypassModal(true)}
                                className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 flex items-center gap-1"
                            >
                                <Lock className="w-4 h-4" />
                                Teacher Bypass
                            </button>
                        )}
                    </div>

                    {/* Progress Bar */}
                    <div className="space-y-2">
                        <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                            <span>Question {currentQuestionIndex + 1} of {totalQuestions}</span>
                            <span>Passing: {passingScore}%</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                            <div
                                className="bg-gradient-to-r from-pink-600 to-purple-600 h-2 rounded-full transition-all"
                                style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
                            />
                        </div>
                    </div>
                </div>

                {/* Question Card */}
                <div className="bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 border-t-0 p-8">
                    <div className="mb-6">
                        <div className="flex items-start gap-3 mb-4">
                            <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400 rounded-full text-sm font-bold">
                                Q{currentQuestionIndex + 1}
                            </span>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white flex-1">
                                {currentQuestion.question}
                            </h2>
                        </div>
                        <p className="text-sm text-gray-500 dark:text-gray-400 ml-14">
                            {currentQuestion.points} {currentQuestion.points === 1 ? 'point' : 'points'}
                        </p>
                    </div>

                    {/* Answer Options */}
                    <div className="space-y-3 mb-8">
                        {currentQuestion.type === 'mcq' && currentQuestion.options && (
                            currentQuestion.options.map((option, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleAnswerChange(index)}
                                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                                        answers[currentQuestion.id] === index
                                            ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20'
                                            : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div
                                            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                                answers[currentQuestion.id] === index
                                                    ? 'border-indigo-600 bg-indigo-600'
                                                    : 'border-gray-400'
                                            }`}
                                        >
                                            {answers[currentQuestion.id] === index && (
                                                <div className="w-2 h-2 bg-white rounded-full" />
                                            )}
                                        </div>
                                        <span className="text-gray-900 dark:text-white">{option}</span>
                                    </div>
                                </button>
                            ))
                        )}

                        {currentQuestion.type === 'true-false' && (
                            <>
                                <button
                                    onClick={() => handleAnswerChange('True')}
                                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                                        answers[currentQuestion.id] === 'True'
                                            ? 'border-green-600 bg-green-50 dark:bg-green-900/20'
                                            : 'border-gray-300 dark:border-gray-600 hover:border-green-400'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <Check className="w-5 h-5 text-green-600" />
                                        <span className="text-gray-900 dark:text-white font-medium">True</span>
                                    </div>
                                </button>
                                <button
                                    onClick={() => handleAnswerChange('False')}
                                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                                        answers[currentQuestion.id] === 'False'
                                            ? 'border-red-600 bg-red-50 dark:bg-red-900/20'
                                            : 'border-gray-300 dark:border-gray-600 hover:border-red-400'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <X className="w-5 h-5 text-red-600" />
                                        <span className="text-gray-900 dark:text-white font-medium">False</span>
                                    </div>
                                </button>
                            </>
                        )}

                        {currentQuestion.type === 'short-answer' && (
                            <input
                                type="text"
                                value={(answers[currentQuestion.id] as string) || ''}
                                onChange={(e) => handleAnswerChange(e.target.value)}
                                placeholder="Enter your answer..."
                                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:border-indigo-600"
                            />
                        )}
                    </div>

                    {/* Navigation */}
                    <div className="flex justify-between items-center">
                        <button
                            onClick={handlePrevious}
                            disabled={currentQuestionIndex === 0}
                            className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Previous
                        </button>

                        <div className="flex gap-2">
                            {questions.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentQuestionIndex(index)}
                                    className={`w-8 h-8 rounded-full text-sm font-medium ${
                                        index === currentQuestionIndex
                                            ? 'bg-indigo-600 text-white'
                                            : answers[questions[index].id] !== undefined
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                            : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                    }`}
                                >
                                    {index + 1}
                                </button>
                            ))}
                        </div>

                        {isLastQuestion ? (
                            <button
                                onClick={handleSubmit}
                                disabled={Object.keys(answers).length < totalQuestions}
                                className="px-6 py-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Submit
                            </button>
                        ) : (
                            <button
                                onClick={handleNext}
                                className="px-6 py-2 bg-gradient-to-r from-pink-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
                            >
                                Next
                            </button>
                        )}
                    </div>
                </div>

                {/* Info Footer */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-b-2xl border border-gray-200 dark:border-gray-700 border-t-0 p-4">
                    <div className="flex items-center gap-2 text-sm text-blue-900 dark:text-blue-300">
                        <AlertCircle className="w-4 h-4" />
                        <span>
                            You have {maxAttempts} {maxAttempts === 1 ? 'attempt' : 'attempts'}. Current attempt: {attemptNumber}
                        </span>
                    </div>
                </div>
            </div>

            {/* Teacher Bypass Modal */}
            {showBypassModal && (
                <TeacherBypassModal
                    onClose={() => setShowBypassModal(false)}
                    onSubmit={handleBypass}
                />
            )}
        </div>
    );
}

// Teacher Bypass Modal
function TeacherBypassModal({
    onClose,
    onSubmit,
}: {
    onClose: () => void;
    onSubmit: (password: string) => void;
}) {
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    const handleSubmit = () => {
        if (password.trim()) {
            onSubmit(password);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Teacher Bypass
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </button>
                </div>

                <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    Enter the teacher password to skip the pre-lab assessment and access the lab directly
                </p>

                <div className="relative mb-6">
                    <input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
                        placeholder="Enter teacher password"
                        className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-600"
                        autoFocus
                    />
                    <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                </div>

                <div className="flex gap-3">
                    <button
                        onClick={onClose}
                        className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!password.trim()}
                        className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Bypass
                    </button>
                </div>
            </div>
        </div>
    );
}
